import type { HomeLook } from "@townsy/contracts";

/**
 * Display names and swatch colours for the free home look choices (ADR-0005 "Free look"). The ids
 * come from the `HomeLook` contract; the hex values are only for the HTML swatches and the 2D
 * preview (the renderer owns its own materials). A unit test keeps these lists equal to the contract.
 */
export interface LookOption<T extends string> {
  id: T;
  label: string;
  hex: string;
}

export const STYLE_OPTIONS: ReadonlyArray<{ id: HomeLook["style"]; label: string; description: string }> = [
  { id: "classic", label: "Classic", description: "White trim and shutters" },
  { id: "modern", label: "Modern", description: "Big windows, clean lines" },
  { id: "rustic", label: "Rustic", description: "Timber beams and stone" },
  { id: "cottage", label: "Cottage", description: "Round door and flower boxes" },
];

export const WALL_COLOR_OPTIONS: ReadonlyArray<LookOption<HomeLook["wallColor"]>> = [
  { id: "white", label: "White", hex: "#F7F6F1" },
  { id: "cream", label: "Cream", hex: "#F2E6C8" },
  { id: "sky", label: "Sky blue", hex: "#BCD7EE" },
  { id: "sage", label: "Sage green", hex: "#BACBAE" },
  { id: "blush", label: "Blush pink", hex: "#EEC8C2" },
  { id: "sand", label: "Sand", hex: "#E0CA9C" },
  { id: "slate", label: "Slate grey", hex: "#8B96A5" },
  { id: "brick", label: "Brick red", hex: "#A6543E" },
];

export const ROOF_COLOR_OPTIONS: ReadonlyArray<LookOption<HomeLook["roofColor"]>> = [
  { id: "charcoal", label: "Charcoal", hex: "#3A3D44" },
  { id: "red", label: "Red", hex: "#A8392F" },
  { id: "green", label: "Green", hex: "#3F6B45" },
  { id: "blue", label: "Blue", hex: "#2F4F86" },
  { id: "brown", label: "Brown", hex: "#6B4A2F" },
  { id: "slate", label: "Slate", hex: "#5D6673" },
];

/** Interior walls use the same colour ids as the exterior walls. */
export const INTERIOR_WALL_OPTIONS: ReadonlyArray<LookOption<HomeLook["interiorWallColor"]>> = WALL_COLOR_OPTIONS;

export const FLOOR_OPTIONS: ReadonlyArray<LookOption<HomeLook["floor"]>> = [
  { id: "wood", label: "Wood", hex: "#B98552" },
  { id: "tile", label: "Tile", hex: "#D9D4CB" },
  { id: "carpet", label: "Carpet", hex: "#7E8BA8" },
  { id: "stone", label: "Stone", hex: "#9C978E" },
];

function find<T extends string>(list: ReadonlyArray<LookOption<T>>, id: T): LookOption<T> {
  return list.find((o) => o.id === id) ?? list[0]!;
}

export const wallHex = (id: HomeLook["wallColor"]) => find(WALL_COLOR_OPTIONS, id).hex;
export const roofHex = (id: HomeLook["roofColor"]) => find(ROOF_COLOR_OPTIONS, id).hex;
export const floorHex = (id: HomeLook["floor"]) => find(FLOOR_OPTIONS, id).hex;

export function styleLabel(id: HomeLook["style"]): string {
  return STYLE_OPTIONS.find((s) => s.id === id)?.label ?? id;
}

/** "Classic style · Cream walls · Charcoal roof". */
export function exteriorLookSummary(look: HomeLook): string {
  return `${styleLabel(look.style)} style · ${find(WALL_COLOR_OPTIONS, look.wallColor).label} walls · ${find(ROOF_COLOR_OPTIONS, look.roofColor).label} roof`;
}

/** "White walls · Wood floor". */
export function interiorLookSummary(look: HomeLook): string {
  return `${find(INTERIOR_WALL_OPTIONS, look.interiorWallColor).label} walls · ${find(FLOOR_OPTIONS, look.floor).label} floor`;
}

export function lookEquals(a: HomeLook, b: HomeLook): boolean {
  return a.style === b.style && a.wallColor === b.wallColor && a.roofColor === b.roofColor && a.interiorWallColor === b.interiorWallColor && a.floor === b.floor;
}
